import React, { useState, useEffect } from "react"
import { Box } from "@mui/material"
import { keyframes } from "@mui/system"
import { SxProps, Theme } from "@mui/material/styles"
import * as Requests from "../../library/requests/Requests"
import { Gallery } from "../../library/Gallery"
import Upload from "../../library/Upload"

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const cellBase: SxProps<Theme> = {
  objectFit: "cover",
  borderRadius: 1,
  width: "100%",
  height: "100%",
}

const transparentPixel =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"

const delays = ["100ms", "850ms", "1250ms", "500ms", "1100ms", "1750ms"]

interface CellDef {
  uploadIndex: number
  size: "thumbnail" | "medium"
  span?: Record<string, string>
}

const spanSx = { gridRow: "span 2", gridColumn: "span 2" }

const mobileCells: CellDef[] = [
  { uploadIndex: 0, size: "thumbnail" },
  { uploadIndex: 1, size: "medium", span: spanSx },
  { uploadIndex: 2, size: "thumbnail" },
  { uploadIndex: 3, size: "thumbnail" },
  { uploadIndex: 4, size: "thumbnail" },
  { uploadIndex: 5, size: "thumbnail" },
]

const desktopCells: CellDef[] = [
  { uploadIndex: 1, size: "medium", span: spanSx },
  { uploadIndex: 0, size: "thumbnail" },
  { uploadIndex: 3, size: "medium", span: spanSx },
  { uploadIndex: 2, size: "thumbnail" },
  { uploadIndex: 4, size: "thumbnail" },
  { uploadIndex: 5, size: "thumbnail" },
]

interface GallerySampleProps {
  gallery: Gallery
  readyThreshold?: number
  fallbackImageUrl?: string
}

function GallerySample({ gallery, readyThreshold = 6, fallbackImageUrl }: GallerySampleProps) {
  const [uploads, setUploads] = useState<Upload[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!gallery.latestUploadsUrl) {
      return
    }

    Requests.getJson(
      gallery.latestUploadsUrl,
      (json: any) => {
        setUploads(json._embedded.uploads)
      },
      (error: string) => {
        setError(error)
      },
    )
  }, [gallery.latestUploadsUrl, setUploads, setError])

  const ready = (uploads.length >= readyThreshold || !!fallbackImageUrl) && !error

  const renderCell = (cell: CellDef, index: number) => {
    const upload = uploads[cell.uploadIndex]
    const src = !ready
      ? transparentPixel
      : upload
        ? upload.media[cell.size]
        : fallbackImageUrl ?? transparentPixel
    return (
      <Box
        key={index}
        component="img"
        src={src}
        alt=""
        sx={{
          ...cellBase,
          opacity: 0,
          ...(ready && {
            animationFillMode: "forwards",
            animationName: `${fadeIn}`,
            animationTimingFunction: "ease-in-out",
            animationDuration: "1000ms",
            animationDelay: delays[cell.uploadIndex],
          }),
          ...cell.span,
        }}
      />
    )
  }

  return (
    <Box sx={{ mx: "15px", my: "30px" }}>
      {/* Mobile: 3x3 grid, large image spans 2x2 */}
      <Box
        sx={{
          display: { xs: "grid", sm: "none" },
          gridTemplateColumns: "repeat(3, 1fr)",
          gridTemplateRows: "repeat(3, 1fr)",
          gap: 0.5,
          aspectRatio: "1",
        }}
      >
        {mobileCells.map(renderCell)}
      </Box>

      {/* Desktop: 2 rows, tall images span 2 rows, short images stack */}
      <Box
        sx={{
          display: { xs: "none", sm: "grid" },
          gridTemplateColumns: "repeat(6, 1fr)",
          gridTemplateRows: "1fr 1fr",
          gap: 0.5,
          aspectRatio: "3",
        }}
      >
        {desktopCells.map(renderCell)}
      </Box>
    </Box>
  )
}

export default GallerySample
